import React, { ChangeEvent, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { SectionHeader, Toggle } from '@/shared/components/export';
import { localPreferencesService } from '@/core/preferences/export';
import { getSettingsSections } from '@/core/settings/services/settings-section-registry';
import SettingsSelectField from '@/core/settings/components/settings-select-field';
import styles from './settings-form.module.css';

declare namespace Intl {
  type Key = 'calendar' | 'collation' | 'currency' | 'numberingSystem' | 'timeZone' | 'unit';

  function supportedValuesOf(input: Key): string[];
}

export default function CalendarTimezoneTab() {
  const [timezone, setTimezone] = useState<string>(dayjs.tz.guess());
  const timeZones = Intl.supportedValuesOf('timeZone');
  const [timezoneType, setTimezoneType] = useState<'local' | 'explicit'>('local');

  useEffect(() => {
    localPreferencesService.getLocalPreferences().then(value => {
      if (value.timezone) setTimezone(value.timezone);
      if (value.timezoneType === 'explicit') setTimezoneType('explicit');
    });
  }, []);

  const onSelectTimezone = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;

    localPreferencesService
      .updateLocalPreferences(async localPreferences => {
        localPreferences.timezone = value;

        return localPreferences;
      })
      .then(() => {
        dayjs.tz.setDefault(value);
        setTimezone(value);
      });
  };

  const onTimezoneTypeChange = (explicit: boolean) => {
    const value: 'local' | 'explicit' = explicit ? 'explicit' : 'local';

    localPreferencesService
      .updateLocalPreferences(async localPreferences => {
        localPreferences.timezoneType = value;

        if (value === 'local') localPreferences.timezone = dayjs.tz.guess();

        return localPreferences;
      })
      .then(localPreferences => {
        setTimezoneType(localPreferences.timezoneType);

        if (localPreferences.timezoneType === 'explicit') {
          setTimezone(localPreferences.timezone);
          dayjs.tz.setDefault(localPreferences.timezone);
        } else {
          setTimezone(dayjs.tz.guess());
          dayjs.tz.setDefault();
        }
      });
  };

  return (
    <div style={{ flex: '1 1 auto', paddingRight: 24 }}>
      <SectionHeader title="Timezone" />

      <div className={styles.fieldRow}>
        <div className={styles.toggleRow}>
          <label className={styles.fieldLabel} style={{ marginBottom: 0 }}>
            Use explicit timezone
          </label>
          <Toggle checked={timezoneType === 'explicit'} onChange={onTimezoneTypeChange} />
        </div>
        <div className={styles.helpText}>
          {timezoneType === 'explicit'
            ? 'Times are shown in the timezone selected below.'
            : "Times are shown in your browser's local timezone."}
        </div>
      </div>

      <SettingsSelectField
        label="Timezone"
        value={timezone}
        onChange={onSelectTimezone}
        disabled={timezoneType !== 'explicit'}
      >
        {timeZones.map(value => (
          <option key={value}>{value}</option>
        ))}
      </SettingsSelectField>

      {getSettingsSections('calendar').map(({ id, title, Component }) => (
        <React.Fragment key={id}>
          <SectionHeader title={title} />

          <div className={styles.fieldRow}>
            <Component />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
